export type ApiAccountsResponse = {
    Accounts: ApiAccount[];
    RecentUsage: Record<number, ApiAccountRecentUsage>;
};

export type ApiAccountListFeature = {
    Id: number;
    ApiAccountId: number;
    ApiFeatureId: ApiFeatureId;
    FeatureName: string;
    FeatureStatus: ApiAccountFeatureStatus;
    TrialExpiration?: string;
};

export type ApiAccountListItem = {
    Id: number;
    CompanyId: string;
    CompanyName: string;
    PrimaryContact: string;
    NotificationEmailAddress: string;
    IsDataSyndication: boolean;
    IsManufacturer: boolean;
    Features: ApiAccountListFeature[];
};

export type ApiAccountsListResponse = {
    Accounts: ApiAccountListItem[];
    RecentUsage: Record<number, ApiAccountRecentUsage>;
};

export type ApiAccount = {
    Id: number;
    CompanyId: string;
    CompanyName: string;
    PrimaryContact: string;
    PrimaryWebsite: string;
    NotificationEmailAddress: string;
    DisplayPricing: boolean;
    InStockOnly: boolean;
    IsActive: boolean;
    IsDataSyndication: boolean;
    IsManufacturer: boolean;
    EnableLifecycleRisk: boolean;
    EnableSupplyChainRisk: boolean;
    EnablePartSpecifications: boolean;
    CORSOrigins: string;
    UpdatedAt: string;

    Features: ApiAccountFeature[];
    DistributorInclusions: ApiAccountDistributor[];
    DistributorExclusions: ApiAccountDistributor[];
    ManufacturerInclusions: ApiAccountManufacturer[];
    Users: ApiAccountUser[];
};

export type ApiAccountFeature = {
    Id?: number;
    ApiAccountId: number;
    ApiFeatureId: ApiFeatureId;
    FeatureName: string;
    FeatureStatus: ApiAccountFeatureStatus;
    Keys: ApiAccountFeatureKey[];
    TrialExpiration?: string;
    IsTrialStatusEmailSent?: boolean;
    IsExpirationWarningEmailSent?: boolean;
    UserFeatureRequestId?: number | null;
    UpdatedAt?: string;
};

export type ApiAccountFeatureKey = {
    Id: number;
    ApiAccountFeatureId: number;
    ApiKeyId: number;
    Key: ApiKey;
};

export type ApiKey = {
    Id: number;
    Key: string;
    IsActive: boolean;
    CreatedAt: string;
};

export enum ApiFeatureId {
    Inventory = 1,
    DSS = 2,
    CarbonEmissions = 3,
    CarbonFootprintWidget = 4,
    InventoryAgent = 5,
}

export enum ApiFeatureName {
    Inventory = "Inventory API",
    DSS = "DSS",
    CarbonEmissions = "Carbon Emissions",
    CarbonFootprintWidget = "Carbon Footprint Widget",
    InventoryAgent = "Inventory AI Agent Service",
}

export const ApiAccountFeatureStatus = {
    ACTIVE: "Active",
    TRIAL: "Trial",
    REQUESTED: "Requested",
    REVOKED: "Revoked",
    DENIED: "Denied",
    SUSPENDED: "Suspended",
} as const;
export type ApiAccountFeatureStatus = (typeof ApiAccountFeatureStatus)[keyof typeof ApiAccountFeatureStatus];

export type ApiAccountDistributor = {
    Id: number;
    ApiAccountId: number;
    DistributorId: number;
    UpdatedAt?: string;
};

export type ApiAccountManufacturer = {
    Id: number;
    ApiAccountId: number;
    ManufacturerId: number;
    UpdatedAt?: string;
};

export type ApiAccountUser = {
    Id: number;
    ApiAccountId: number;
    UserId: number;
    CreatedAt?: string;
    UpdatedAt?: string;
    User: User;
};

export type ApiAccountRecentUsage = {
    CompanyId: string;
    APIv1SearchCount: number;
    APIv2SearchCount: number;
    DSSSearchCount: number;
    LastRequest?: string;
};

export type User = {
    Id: number;
    Guid: string;
    CognitoId: string;
    UserName: string;
    Name: string;
    CompanyName: string;
    EmailAddress: string;
    PhoneNumber: string;
    BusinessEmail: string;
    PrimaryWebsite: string;
    AddressLine1: string;
    AddressLine2: string;
    City: string;
    State: string;
    ZipCode: string;
    CountryId: number | null;
    LanguageCode: string;
    CurrencyId: number | null;
    IsVerified: boolean;
    IsEmailVerified: boolean;
    DeletedDate: string | null;
};

export type ApiAccountCompanies = {
    Distributors: { Id: number; Name: string }[];
    Manufacturers: { Id: number; Name: string }[];
    NonParticipantCompanies: { Id: number; Name: string }[];
};

export type ApiTrialExtensionFeature = {
    Id: number;
    ApiAccountId: number;
    ApiFeatureId: number;
    FeatureStatus: string;
    TrialExpiration: string | null;
    FeatureName: string;
    Account: Omit<ApiAccountListItem, "Features"> & { TypeLabel: string };
};

export type ApiTrialExtensionResponse = {
    Id: number;
    ApiAccountFeatureId: number;
    IsInProduction: boolean;
    Processed: boolean;
    Comments: string | null;
    SuggestedTrialExpiration: string | null;
    CreatedAt: string;
    UpdatedAt: string;
    ApiAccountFeature: ApiTrialExtensionFeature;
};
